import { useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { dataCache } from '../utils/dataCache';

/**
 * Hook para gerenciar sincronização e revalidação de dados
 * Resolve problemas de cache e dados desatualizados
 * Detecta refresh da página (F5) e força recarregamento completo
 */
export const useDataSync = () => {
  const { user } = useAuth();
  const revalidationCallbacks = useRef<Set<() => Promise<void>>>(new Set());
  const lastRevalidation = useRef<number>(0);
  const isRevalidating = useRef<boolean>(false);
  const pageLoadTime = useRef<number>(Date.now());
  const hasDetectedRefresh = useRef<boolean>(false);
  const refreshProcessed = useRef<boolean>(false);
  const refreshTimestamp = useRef<number>(0);
  const dashboardsProcessed = useRef<Set<string>>(new Set());
  const lastFocusRevalidation = useRef<number>(0);
  const lastVisibilityRevalidation = useRef<number>(0);

  // Registrar callback de revalidação
  const registerRevalidation = useCallback((callback: () => Promise<void>) => {
    revalidationCallbacks.current.add(callback);
    
    // Retornar função para desregistrar
    return () => {
      revalidationCallbacks.current.delete(callback);
    };
  }, []);

  // Detectar se a página foi recarregada (F5 ou refresh)
  const detectPageRefresh = useCallback(() => {
    // Usar performance.navigation para detectar refresh de forma mais confiável
    const isRefresh = performance.navigation?.type === 1 || 
                     (performance.getEntriesByType('navigation')[0] as any)?.type === 'reload';
    
    // Verificar se há dados no sessionStorage indicando que não é primeira carga
    const lastPageLoad = sessionStorage.getItem('saudevirtual_last_page_load');
    const currentTime = Date.now();
    
    console.log('🔍 [useDataSync] Detectando refresh:', {
      isRefresh,
      hasLastPageLoad: !!lastPageLoad,
      timeSinceLastLoad: lastPageLoad ? currentTime - parseInt(lastPageLoad) : 'N/A'
    });
    
    if (isRefresh || !lastPageLoad) {
      console.log('🔄 Refresh da página detectado - forçando recarregamento completo');
      hasDetectedRefresh.current = true;
      refreshProcessed.current = false; // Reset para permitir nova execução
      refreshTimestamp.current = currentTime; // Timestamp único para este refresh
      dashboardsProcessed.current.clear(); // Limpar dashboards processados
      
      // Limpar cache para forçar busca no banco
      dataCache.clear();
      
      // Atualizar timestamp da última carga
      sessionStorage.setItem('saudevirtual_last_page_load', currentTime.toString());
      
      return true;
    }
    
    // Atualizar timestamp se não foi refresh
    sessionStorage.setItem('saudevirtual_last_page_load', currentTime.toString());
    return false;
  }, []);

  // Executar revalidação de todos os callbacks registrados
  const revalidateAll = useCallback(async (force = false, isRefresh = false) => {
    if (!user) return;
    
    const now = Date.now();
    const timeSinceLastRevalidation = now - lastRevalidation.current;
    
    // Para refresh, sempre executar independente do tempo
    if (!force && !isRefresh && timeSinceLastRevalidation < 5000) {
      console.log('⏭️ Revalidação ignorada - muito recente');
      return;
    }
    
    if (isRevalidating.current) {
      console.log('⏭️ Revalidação já em andamento');
      return;
    }
    
    try {
      isRevalidating.current = true;
      lastRevalidation.current = now;
      
      if (isRefresh) {
        console.log('🔄 Iniciando revalidação COMPLETA após refresh da página...');
      } else {
        console.log('🔄 Iniciando revalidação global de dados...');
      }
      
      // Executar todos os callbacks de revalidação em paralelo
      const promises = Array.from(revalidationCallbacks.current).map(callback => 
        callback().catch(error => {
          console.error('❌ Erro na revalidação:', error);
        })
      );
      
      await Promise.all(promises);
      
      if (isRefresh) {
        console.log('✅ Revalidação COMPLETA após refresh concluída');
      } else {
        console.log('✅ Revalidação global concluída');
      }
    } finally {
      isRevalidating.current = false;
    }
  }, [user]);

  // Detectar refresh da página na inicialização
  useEffect(() => {
    if (!user) return;

    console.log('🚀 [useDataSync] Inicializando detecção de refresh para usuário:', user.id);
    
    // Detectar se foi um refresh e forçar revalidação
    const wasRefresh = detectPageRefresh();
    if (wasRefresh && !refreshProcessed.current) {
      console.log('🔄 [useDataSync] Refresh detectado - agendando revalidação completa...');
      refreshProcessed.current = true; // Marcar como processado
      
      // Aguardar um pouco para garantir que todos os hooks estejam prontos
      setTimeout(() => {
        console.log('🔄 [useDataSync] Executando revalidação completa após refresh...');
        revalidateAll(true, true).finally(() => {
          // Resetar flag imediatamente após primeira execução
          setTimeout(() => {
            hasDetectedRefresh.current = false;
            console.log('✅ [useDataSync] Flag de refresh resetada imediatamente');
          }, 100); // Reset quase imediato para evitar múltiplas execuções
        });
      }, 1000);
    } else {
      console.log('✅ [useDataSync] Carregamento normal da página - sem refresh detectado');
    }
  }, [user, detectPageRefresh, revalidateAll]);

  // Revalidação automática em intervalos
  useEffect(() => {
    if (!user) return;

    const interval = setInterval(() => {
      revalidateAll();
    }, 60000); // Revalidar a cada 1 minuto

    return () => clearInterval(interval);
  }, [user, revalidateAll]);

  // Revalidação ao focar na janela (usuário voltou para a aplicação)
  // Com throttling para evitar loops excessivos
  useEffect(() => {
    const FOCUS_THROTTLE_MS = 15000; // 15 segundos mínimo entre revalidações de foco
    const VISIBILITY_THROTTLE_MS = 10000; // 10 segundos mínimo entre revalidações de visibilidade

    const handleFocus = () => {
      const now = Date.now();
      const timeSinceLastFocus = now - lastFocusRevalidation.current;
      
      console.log('👁️ [useDataSync] Janela focada:', {
        timeSinceLastFocus,
        throttleMs: FOCUS_THROTTLE_MS,
        willRevalidate: timeSinceLastFocus >= FOCUS_THROTTLE_MS
      });
      
      // Só revalidar se passou tempo suficiente desde a última revalidação de foco
      if (timeSinceLastFocus >= FOCUS_THROTTLE_MS) {
        lastFocusRevalidation.current = now;
        console.log('🔄 [useDataSync] Revalidando dados após foco (throttled)...');
        revalidateAll(true);
      } else {
        console.log('⏭️ [useDataSync] Foco ignorado - throttling ativo');
      }
    };

    const handleVisibilityChange = () => {
      if (!document.hidden) {
        const now = Date.now();
        const timeSinceLastVisibility = now - lastVisibilityRevalidation.current;
        
        console.log('👁️ [useDataSync] Página visível:', {
          timeSinceLastVisibility,
          throttleMs: VISIBILITY_THROTTLE_MS,
          willRevalidate: timeSinceLastVisibility >= VISIBILITY_THROTTLE_MS
        });
        
        // Só revalidar se passou tempo suficiente desde a última revalidação de visibilidade
        if (timeSinceLastVisibility >= VISIBILITY_THROTTLE_MS) {
          lastVisibilityRevalidation.current = now;
          console.log('🔄 [useDataSync] Revalidando dados após visibilidade (throttled)...');
          revalidateAll(true);
        } else {
          console.log('⏭️ [useDataSync] Visibilidade ignorada - throttling ativo');
        }
      }
    };

    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [revalidateAll]);

  // Forçar revalidação completa (para uso manual)
  const forceCompleteRevalidation = useCallback(async () => {
    console.log('🔄 Forçando revalidação completa manual...');
    dataCache.clear();
    await revalidateAll(true, true);
  }, [revalidateAll]);

  // Função para marcar dashboard como processado
  const markDashboardProcessed = useCallback((dashboardType: string) => {
    const key = `${dashboardType}_${refreshTimestamp.current}`;
    dashboardsProcessed.current.add(key);
    console.log(`📝 [useDataSync] Dashboard ${dashboardType} marcado como processado para refresh ${refreshTimestamp.current}`);
  }, []);

  // Função para verificar se dashboard já foi processado
  const isDashboardProcessed = useCallback((dashboardType: string) => {
    const key = `${dashboardType}_${refreshTimestamp.current}`;
    const processed = dashboardsProcessed.current.has(key);
    console.log(`🔍 [useDataSync] Dashboard ${dashboardType} já processado: ${processed}`);
    return processed;
  }, []);

  return {
    registerRevalidation,
    revalidateAll,
    forceCompleteRevalidation,
    detectPageRefresh,
    markDashboardProcessed,
    isDashboardProcessed,
    refreshTimestamp: refreshTimestamp.current,
    isRevalidating: isRevalidating.current,
    hasDetectedRefresh: hasDetectedRefresh.current
  };
};

export default useDataSync;